import { Controller } from '@hotwired/stimulus'
import {
  getSelectedIds,
  updateHiddenInputFromIds,
  addSelection,
  removeSelection,
  clearAllSelections
} from './selection_utils'

function isMissing (el: unknown): el is null | undefined {
  return el === null || typeof el === 'undefined'
}

export default class extends Controller {
  bulkUpdating = false
  domainCounts: Map<string, number> = new Map()
  persistentSelectedIds: Set<string> = new Set()

  connect (): void {
    setTimeout(() => {
      const selectedIds = getSelectedIds()
      this.persistentSelectedIds = new Set(selectedIds)

      this.restoreCheckboxStates(selectedIds)
      this.updateAllDomainCheckboxStates()
      this.updateSelectAllCheckbox()
      this.updateTotalControlCount()
    }, 100)

    const domainSearchInput = document.querySelector<HTMLInputElement>('#sticky-search-form-domains input[type="search"]')
    const controlSearchInput = document.querySelector<HTMLInputElement>('#sticky-search-form-controls input[type="search"]')

    domainSearchInput?.addEventListener('input', this.handleDomainSearchChange)
    controlSearchInput?.addEventListener('input', this.handleDomainSearchChange)
    this.handleDomainSearchChange()

    document.addEventListener('turbo:frame-load', this.handleTurboFrameLoad)
    document.addEventListener('change', this.handleControlToggleFromUser)
    document.addEventListener('controls-checkboxes-restored', this.syncAfterRestoring)
  }

  disconnect (): void {
    const domainSearchInput = document.querySelector<HTMLInputElement>('#sticky-search-form-domains input[type="search"]')
    const controlSearchInput = document.querySelector<HTMLInputElement>('#sticky-search-form-controls input[type="search"]')

    domainSearchInput?.removeEventListener('input', this.handleDomainSearchChange)
    controlSearchInput?.removeEventListener('input', this.handleDomainSearchChange)

    document.removeEventListener('turbo:frame-load', this.handleTurboFrameLoad)
    document.removeEventListener('change', this.handleControlToggleFromUser)
    document.removeEventListener('controls-checkboxes-restored', this.syncAfterRestoring)
  }

  syncAfterRestoring = (): void => {
    this.updateAllDomainCheckboxStates()
    this.updateSelectAllCheckbox()
    this.updateTotalControlCount()
  }

  handleTurboFrameLoad = (): void => {
    if (!this.element.isConnected) return

    setTimeout(() => {
      this.restoreCheckboxStates(Array.from(this.persistentSelectedIds))
      this.updateAllDomainCheckboxStates()
      this.updateSelectAllCheckbox()
      this.updateTotalControlCount()
    }, 100)
  }

  toggleSelectAll (event: Event): void {
    this.bulkUpdating = true

    const selectAll = event.target as HTMLInputElement
    const checked = selectAll.checked
    let totalSelected = 0

    this.domainCheckboxes.forEach(domainCheckbox => {
      domainCheckbox.checked = checked
      const domainId = domainCheckbox.value
      const controlIds = this.getControlIdsFromCheckbox(domainCheckbox)

      controlIds.forEach(controlId => {
        const control = this.getControlCheckbox(controlId)
        if (control != null) {
          control.checked = checked
          control.disabled = false
        }

        if (checked) {
          this.persistentSelectedIds.add(controlId)
        } else {
          this.persistentSelectedIds.delete(controlId)
        }
      })

      const badge = document.querySelector<HTMLSpanElement>(`[data-domain-count-for="${domainId}"]`)
      if (badge != null) badge.textContent = checked ? `${controlIds.length}` : '0'

      if (checked) {
        totalSelected += controlIds.length
      }
    })

    updateHiddenInputFromIds(Array.from(this.persistentSelectedIds))
    this.updateSelectAllCheckbox()

    const totalBadge = document.querySelector<HTMLSpanElement>('[data-all-domains-count]')
    if (totalBadge != null) totalBadge.textContent = `${checked ? totalSelected : 0}`

    setTimeout(() => {
      this.bulkUpdating = false
    }, 0)
  }

  toggleDomain (event: Event): void {
    this.bulkUpdating = true

    const domainCheckbox = event.target as HTMLInputElement
    const domainId = domainCheckbox.value
    const checked = domainCheckbox.checked
    const controlIds = this.getControlIdsFromCheckbox(domainCheckbox)

    controlIds.forEach(controlId => {
      const control = this.getControlCheckbox(controlId)
      if (control != null) {
        control.checked = checked
        control.disabled = false
      }

      if (checked) {
        this.persistentSelectedIds.add(controlId)
      } else {
        this.persistentSelectedIds.delete(controlId)
      }
    })

    updateHiddenInputFromIds(Array.from(this.persistentSelectedIds))
    this.syncDomainCheckboxFromControls(domainId)
    this.updateAllDomainCheckboxStates()
    this.updateTotalControlCount()
    this.updateSelectAllCheckbox()

    this.bulkUpdating = false
  }

  restoreCheckboxStates (selectedIds: string[]): void {
    selectedIds.forEach(id => {
      const checkbox = this.getControlCheckbox(id)
      if (checkbox != null) checkbox.checked = true
    })
  }

  updateAllDomainCheckboxStates (): void {
    this.domainCheckboxes.forEach(domainCheckbox => {
      const domainId = domainCheckbox.value
      const controlIds = this.getControlIdsFromCheckbox(domainCheckbox)
      const selectedIds = Array.from(this.persistentSelectedIds)
      const allSelected = controlIds.every(id => selectedIds.includes(id))
      domainCheckbox.checked = allSelected
      this.updateDomainCount(domainId)
    })
  }

  updateDomainCount (domainId: string): void {
    const domainCheckbox = this.getDomainCheckbox(domainId)
    if (domainCheckbox == null) return

    const controlIds = this.getControlIdsFromCheckbox(domainCheckbox)
    const count = controlIds.filter(id => this.persistentSelectedIds.has(id)).length

    this.domainCounts.set(domainId, count)

    const badge = document.querySelector<HTMLSpanElement>(`[data-domain-count-for="${domainId}"]`)
    if (badge != null) badge.textContent = `${count}`
  }

  updateSelectAllCheckbox (): void {
    const selectAll = document.getElementById('select-all-domains') as HTMLInputElement | null
    const allChecked = this.domainCheckboxes.every(cb => cb.checked)
    if (selectAll != null) selectAll.checked = allChecked
  }

  clearSelections (): void {
    clearAllSelections()
    this.persistentSelectedIds.clear()

    this.domainCheckboxes.forEach(cb => (cb.checked = false))
    document.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-domain-id]:not(.domain-checkbox)').forEach(cb => {
      cb.checked = false
      cb.disabled = false
    })

    document.querySelectorAll<HTMLSpanElement>('[data-domain-count-for]').forEach(span => {
      span.textContent = '0'
    })

    const selectAll = document.getElementById('select-all-domains') as HTMLInputElement | null
    if (selectAll != null) selectAll.checked = false

    this.updateTotalControlCount()
    updateHiddenInputFromIds([])
  }

  get domainCheckboxes (): HTMLInputElement[] {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input.domain-checkbox'))
  }

  getDomainCheckbox (domainId: string): HTMLInputElement | null {
    return document.querySelector<HTMLInputElement>(`input.domain-checkbox[value="${domainId}"]`)
  }

  getControlIdsFromCheckbox (domainCheckbox: HTMLInputElement): string[] {
    const attr = domainCheckbox.dataset.controlIds ?? ''
    return attr.trim() !== '' ? attr.split(',').map(id => id.trim()) : []
  }

  getControlsForDomain (domainId: string): HTMLInputElement[] {
    const domainCheckbox = this.getDomainCheckbox(domainId)
    if (domainCheckbox == null) return []

    const controlIds = this.getControlIdsFromCheckbox(domainCheckbox)
    return controlIds.map(id => this.getControlCheckbox(id)).filter((cb): cb is HTMLInputElement => cb != null)
  }

  getControlCheckbox (controlId: string): HTMLInputElement | null {
    return document.querySelector<HTMLInputElement>(`input[type="checkbox"][value="${controlId}"]:not(.domain-checkbox)`)
  }

  handleControlToggleFromUser = (event: Event): void => {
    const checkbox = event.target as HTMLInputElement | null

    if (checkbox === null) return
    if (checkbox.type !== 'checkbox') return
    if (checkbox.classList.contains('domain-checkbox')) return

    const domainId = checkbox.dataset.domainId
    if (typeof domainId !== 'string' || domainId.trim() === '') return

    const controlId = checkbox.value

    if (checkbox.checked) {
      addSelection(controlId)
    } else {
      removeSelection(controlId)
    }

    this.syncDomainCheckboxFromControls(domainId)
    this.updateAllDomainCheckboxStates()
    this.updateTotalControlCount()
  }

  syncDomainCheckboxFromControls (domainId: string): void {
    const controls = this.getControlsForDomain(domainId)
    const domainCheckbox = this.getDomainCheckbox(domainId)

    const allChecked = controls.length > 0 && controls.every(cb => cb.checked)
    if (domainCheckbox !== null) {
      domainCheckbox.checked = allChecked
    }

    this.updateDomainCount(domainId)
    this.updateSelectAllCheckbox()
  }

  updateTotalControlCount (): void {
    let total = 0
    for (const count of this.domainCounts.values()) {
      total += count
    }

    const totalBadge = document.querySelector<HTMLSpanElement>('[data-all-domains-count]')
    if (totalBadge != null) totalBadge.textContent = `${total}`
  }

  handleDomainSearchChange (): void {
    const domainSearchInput = document.querySelector<HTMLInputElement>('#sticky-search-form-domains input[type="search"]')
    const controlSearchInput = document.querySelector<HTMLInputElement>('#sticky-search-form-controls input[type="search"]')
    const selectAllCheckbox = document.querySelector<HTMLInputElement>('#select-all-domains')
    const totalCountBadge = document.querySelector<HTMLElement>('[data-all-domains-count]')

    if (
      isMissing(domainSearchInput) ||
      isMissing(controlSearchInput) ||
      isMissing(selectAllCheckbox) ||
      isMissing(totalCountBadge)
    ) {
      return
    }

    const isSearching = domainSearchInput.value.trim() !== '' || controlSearchInput.value.trim() !== ''

    selectAllCheckbox.disabled = isSearching
    totalCountBadge.style.display = isSearching ? 'none' : ''
  }
}
